import type { Book } from '@/types/book';

/**
 * Pick which book stays selected after the books list (re)loads.
 *
 * Order matters and used to be the other way round (`active ?? previous ?? first`), which lost a
 * deep-link (T-D01). Opening `/book/<B>/chapter/<C>` calls `selectBook(B)`, which sets the current
 * book AND pushes `active_book_id = B` to the profile — but that write is async. Any reload of the
 * list that ran before the profile came back read the OLD `active_book_id` (say book A) and, with
 * `active` winning, replaced the deep-linked B with A. The user asked for one book and got another.
 *
 * Keeping the CURRENT selection first fixes that without weakening session restore: on a cold start
 * `previous` is null, so `active_book_id` still decides which book opens.
 *
 * Deliberate consequence: once a book is selected in this tab, a changed `active_book_id` (e.g. the
 * same account opened elsewhere) no longer yanks the book out from under the writer. Switching books
 * stays an explicit action.
 */
export function resolveCurrentBook(
  previous: Book | null,
  nextBooks: Book[],
  activeBookId: string | null | undefined,
): Book | null {
  // Both lookups re-read from nextBooks so a stale object (or a deleted book) never survives.
  const previousBook = previous ? nextBooks.find((book) => book.id === previous.id) ?? null : null;
  const activeBook = activeBookId ? nextBooks.find((book) => book.id === activeBookId) ?? null : null;

  return previousBook ?? activeBook ?? nextBooks[0] ?? null;
}
